<template>
  <div class="table-of-contents">
    <div class="table-of-contents__sidebar">
      <CoreTableOfContents :elem="article.parsed_content[0]" />
    </div>
    <main class="table-of-contents__main">
      <div class="table-of-contents__header">
        <ArticleCard
          class="table-of-contents__header-card"
          :article="article"
        />
      </div>
      <div
        class="table-of-contents__content table-of-contents__content--article-content default-article__analytics"
      >
        <div
          id="article-content"
          class="table-of-contents__content-wrapper"
        >
          <template
            v-for="(content, index) in slicedContents"
            :key="index"
          >
            <TheContent
              v-for="item of content"
              :key="item.id"
              :elem="item"
            />
            <WidgetsArea
              v-if="index === 0"
              area="after-first-paragraph"
            />
            <!-- виджет не должен быть показан в конце, если это последний чанк -->
            <WidgetsArea
              v-else-if="index !== slicedContents.length - 1"
              :area="'post-dynamic'"
              :index-sliced-content="index"
            />
          </template>
        </div>
        <ArticleFooter class="table-of-contents__footer" />
      </div>
      <div class="table-of-contents__content table-of-contents__content--bottom">
        <div class="table-of-contents__content-wrapper">
          <WidgetsArea area="above-comments" />

          <!-- комментарии -->
          <CommentsContainer
            v-if="showComments"
            :id="article.id"
            class="table-of-contents__comments comments"
          />

          <WidgetsArea area="below-comments" />
        </div>
      </div>
    </main>
  </div>
</template>

<script lang="ts">
  import TheContent from '~/components/TheContent';
  import CommentsContainer from '~/components/CommentsContainer.vue';
  import CoreTableOfContents from '~/components/CoreTableOfContents.vue';
  import ArticleCard from '~/components/ArticleCard.vue';
  import type { IFullArticle, TContent } from '~/types/common';
  import ArticleFooter from '~/components/ArticleFooter.vue';
  import WidgetsArea from '~/components/WidgetsArea/WidgetsArea.vue';
  import { useSingleArticleStore } from '~/store/singleArticle';
  import { getSplicedContent } from '@devhacker/shared/utils/adfox';

  export default defineNuxtComponent({
    name: 'TemplateTableOfContents',

    components: {
      TheContent,
      ArticleCard,
      ArticleFooter,
      WidgetsArea,
      CoreTableOfContents,
      CommentsContainer,
    },

    provide() {
      return {
        isEndArticleReadAlso: (): boolean => {
          return Boolean(this.article?.parsed_content?.at(-1)?.type === 'burninghut__read-also');
        },

        rubricColor: (): string => {
          return this.article?.categories[0].color || '#EBEBEB';
        },

        isPartner: (): boolean => {
          return !!this.article?.is_advertising;
        },
      };
    },

    computed: {
      slicedContents(): TContent[][] {
        const spliced = getSplicedContent(this.article.parsed_content, 'bh-kiss__paragraph', 7);
        return spliced;
      },

      article() {
        const article = useSingleArticleStore().article;

        if (!article) {
          throw createError({ status: 500, message: `Article hasn't been provided from pinia` });
        }

        return article as IFullArticle;
      },

      showComments(): boolean | undefined {
        return this.article?.comments_enabled && !this.article?.is_promo;
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use '~/scss/article' as *;

  @mixin contentSpacing {
    @include tablet {
      padding: $spacing-0;
      margin: $spacing-0 auto;
    }

    @include big-tablet {
      width: 592px;
      margin: $spacing-6 auto;
    }

    @include desktop {
      width: 732px;
    }
  }

  .table-of-contents {
    @include container;
    padding-bottom: $spacing-8;
    background: #fff;

    @include tablet {
      padding-bottom: $spacing-10;
      margin-top: 24px !important;
    }

    @include big-tablet {
      padding-left: $spacing-8;
      padding-right: $spacing-8;
      max-width: unset;
      display: grid;
      grid-template-columns: 300px auto;
      grid-column-gap: 20px;
    }

    @include desktop {
      padding-left: $spacing-9;
      padding-right: $spacing-9;
      max-width: 1280px;
      grid-column-gap: 24px;
    }

    &__header {
      margin-bottom: $spacing-6;

      @include tablet {
        margin-bottom: $spacing-8;
      }

      @include big-tablet {
        margin-bottom: $spacing-6;
      }

      &-card {
        @include big-tablet {
          padding-left: $spacing-4;
          padding-right: $spacing-4;
        }

        :deep() {
          .article-card__content {
            @include contentSpacing;

            @include tablet {
              padding-bottom: $spacing-4;
            }
          }
        }
      }
    }

    &__sidebar {
      position: sticky;
      top: $spacing-15;
      overflow: hidden;
      height: min-content;
      max-height: calc(100vh - 65px);
      display: none;
      border: 1px solid #000000;
      border-radius: 32px;

      @include big-tablet {
        display: flex;
      }
    }

    &__main {
      font-family: $text-font--1;
      max-width: 100%;
    }

    &__content {
      @include contentSpacing;

      &--bottom {
        padding: 0;
        margin-top: $spacing-8;
        @include tablet {
          margin-top: $spacing-10;
        }

        .widgets-area {
          &:first-child {
            margin-top: $spacing-0;
          }

          @include contentSpacing;
        }
      }
    }

    &__footer {
      background: #fff;
      border-top: 0;
      border-radius: 0 0 34px 34px;
      margin-top: $spacing-12;

      @include tablet {
        border-radius: 0 0 32px 32px;
        border: 0px;
      }
    }

    .table-of-contents__content--bottom > .table-of-contents__content-wrapper,
    .table-of-contents__footer {
      padding-left: $spacing-4;
      padding-right: $spacing-4;

      @include tablet {
        padding: $spacing-0 $spacing-16;
      }

      @include big-tablet {
        padding: $spacing-0;
      }
    }

    .table-of-contents__content-wrapper {
      padding-left: $spacing-4;
      padding-right: $spacing-4;

      @include tablet {
        padding: 0 $spacing-16;
      }

      @include big-tablet {
        padding: 0;
      }

      @include article-picture-styles;

      :deep() > {
        @include lead-styles;
        @include article-elems;
      }

      :deep(.core-table-of-contents) {
        @include big-tablet {
          display: none;
        }
      }

      :deep() {
        .widgets-area {
          .accent-cards {
            .accent-cards__wrapper--2,
            .accent-cards__wrapper--4 {
              .accent-card--small {
                @include big-tablet {
                  max-height: 348px;
                  min-height: 348px;
                }
                @include desktop {
                  min-height: 400px;
                  max-height: 440px;
                }
              }
            }
          }
        }
      }
    }

    &__headline {
      margin-top: $spacing-2;
      font-family: $headline-font--1;
      font-weight: 400;
      @include headline-normal-text;
      margin-bottom: $spacing-3;

      @include tablet {
        margin-top: $spacing-4;
        @include headline-big-text;
      }
    }
  }

  .comments {
    padding: 0;
    margin: 32px auto 0;

    @include tablet {
      margin: 40px auto 0;
    }

    &:not(:last-child) {
      margin-bottom: $spacing-8;
      @include tablet {
        margin-bottom: $spacing-10;
      }
    }
    @include big-tablet {
      width: 592px;
    }
    @include desktop {
      width: 732px;
    }

    :deep() .lh-talker-app {
      margin-top: 0;
      margin-bottom: 0;

      .form {
        > *:first-child {
          border: 2px solid $stroke-color--1;

          @include hover {
            border-color: #699bdc;
          }
        }
      }
    }
  }
</style>
